import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import useMeta from "@/hooks/useMeta";

const BlogPost = ({
  apiPath = "/blog/posts",
  basePath = "/blog",
  backLabel = "Blog",
  relatedHeading = "More insights from Dr. Campbell",
  moreCta = "Back to all articles",
  testIdPrefix = "blog-post",
}) => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    setStatus("loading");
    setPost(null);
    api.get(`${apiPath}/${slug}`).then(({ data }) => {
      setPost(data);
      setStatus("ready");
    }).catch(() => setStatus("missing"));
  }, [slug, apiPath]);

  useEffect(() => {
    api.get(apiPath).then(({ data }) => {
      setRelated((data.posts || []).filter((p) => p.slug !== slug).slice(0, 3));
    }).catch(() => setRelated([]));
  }, [slug, apiPath]);

  const canonical = post?.canonical_url || (typeof window !== "undefined"
    ? `${window.location.origin}${basePath}/${slug}` : undefined);

  useMeta({
    title: post ? (post.meta_title || post.title) : `${backLabel} — Global Leader Solutions`,
    description: post?.description,
    image: post?.image,
    canonical,
    jsonLd: post ? {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": post.title,
      "description": post.description,
      "image": post.image,
      "datePublished": post.published_at,
      "dateModified": post.updated_at,
      "author": { "@type": "Person", "name": post.author || "Dr. Andrew Campbell" },
      "publisher": {
        "@type": "Organization",
        "name": "Global Leader Solutions",
        "logo": { "@type": "ImageObject", "url": "https://globalleadersolutions.com/logo.png" },
      },
      "mainEntityOfPage": { "@type": "WebPage", "@id": canonical },
      "keywords": post.target_keyword,
    } : undefined,
  });

  if (status === "loading") {
    return <div className="max-w-3xl mx-auto px-4 py-24 text-sm text-[#6B5E62]" data-testid={`${testIdPrefix}-loading`}>Loading…</div>;
  }
  if (status === "missing" || !post) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid={`${testIdPrefix}-missing`}>
        <h1 className="text-3xl font-serif font-semibold text-[#2D2628] mb-4">Post not found</h1>
        <p className="text-sm text-[#6B5E62] mb-6">The article you're looking for isn't available.</p>
        <Link to={basePath} className="text-sm uppercase tracking-widest font-semibold text-[#8C4A57] hover:text-[#703B45]">
          ← Back to {backLabel}
        </Link>
      </div>
    );
  }

  return (
    <div data-testid={`${testIdPrefix}-${post.slug}`} className="bg-white">
      <section className="bg-[#FAF4F5] border-b border-[#E8DCDD]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <Link to={basePath} className="text-xs uppercase tracking-widest font-semibold text-[#8C4A57] hover:text-[#703B45]" data-testid={`${testIdPrefix}-back-link`}>
            ← {backLabel}
          </Link>
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#8C4A57] mt-6 mb-4">Article</div>
          <h1 data-testid={`${testIdPrefix}-title`} className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold text-[#2D2628] leading-[1.1]">
            {post.title}
          </h1>
          {post.subtitle && post.subtitle !== post.title && (
            <p className="mt-5 text-base sm:text-lg text-[#6B5E62] leading-relaxed">{post.subtitle}</p>
          )}
          <p className="mt-6 text-xs uppercase tracking-widest text-[#6B5E62]">
            By {post.author || "Dr. Andrew Campbell"}
            {post.published_at && ` · ${new Date(post.published_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`}
          </p>
        </div>
      </section>

      {post.image && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 sm:-mt-10 mb-8 relative z-10">
          <img data-testid={`${testIdPrefix}-hero-image`} src={post.image} alt={post.image_alt || post.title}
            className="w-full aspect-[16/9] object-cover rounded-sm shadow-lg" />
        </div>
      )}

      <article data-testid={`${testIdPrefix}-body`}
        className="blog-content max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14"
        dangerouslySetInnerHTML={{ __html: post.body_html }} />

      {related.length > 0 && (
        <section className="border-t border-[#E8DCDD] bg-white" data-testid={`${testIdPrefix}-related`}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#8C4A57] mb-3">Related Articles</div>
            <h2 className="text-2xl sm:text-3xl font-serif font-semibold text-[#2D2628] mb-8">{relatedHeading}</h2>
            <div className={`grid grid-cols-1 ${related.length > 1 ? "md:grid-cols-2 lg:grid-cols-3" : ""} gap-6`}>
              {related.map((r) => (
                <article key={r.id} data-testid={`${testIdPrefix}-related-card-${r.slug}`}
                  className="group border border-[#E8DCDD] rounded-md overflow-hidden bg-white hover:border-[#8C4A57] transition-colors duration-200">
                  <Link to={`${basePath}/${r.slug}`}
                    onClick={() => {
                      if (typeof window !== "undefined" && typeof window.gtag === "function") {
                        window.gtag("event", "related_click", {
                          from_slug: slug,
                          to_slug: r.slug,
                          section: testIdPrefix,
                        });
                      }
                    }}
                    className="block">
                    {r.image && (
                      <div className="w-full aspect-[16/9] bg-[#FAF4F5] overflow-hidden">
                        <img src={r.image} alt={r.image_alt || r.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      </div>
                    )}
                    <div className="p-5">
                      <h3 className="text-lg font-serif font-semibold text-[#2D2628] leading-snug mb-2 group-hover:text-[#8C4A57] transition-colors">
                        {r.title}
                      </h3>
                      <p className="text-xs text-[#6B5E62] leading-relaxed line-clamp-2">{r.excerpt || r.description}</p>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-[#FAF4F5] border-t border-[#E8DCDD]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center">
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#8C4A57] mb-3">Keep Reading</div>
          <Link to={basePath} className="text-sm uppercase tracking-widest font-semibold text-[#8C4A57] hover:text-[#703B45]" data-testid={`${testIdPrefix}-more-link`}>
            {moreCta}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
